import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { api, useSettings } from "../../lib/api";

const fields: { key: string; label: string }[] = [
  { key: "company_name", label: "Company Name" },
  { key: "tagline", label: "Tagline" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "hours", label: "Business Hours" },
  { key: "uei", label: "UEI" },
  { key: "cage", label: "CAGE Code" },
  { key: "founder_name", label: "Founder Name" },
  { key: "founder_title", label: "Founder Title" },
];

export const SettingsPanel = () => {
  const { data: settings } = useSettings();
  const [form, setForm] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (settings && !form) setForm({ ...settings });
  }, [settings, form]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (typeof payload.naics === "string")
        payload.naics = payload.naics.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (typeof payload.certifications === "string")
        payload.certifications = payload.certifications.split(",").map((s: string) => s.trim()).filter(Boolean);
      await api.put("/admin/settings", payload);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="text-sm text-sub">Loading…</p>;

  return (
    <div data-testid="settings-panel">
      <p className="overline-label mb-2">Configuration</p>
      <h1 className="font-heading font-medium text-3xl tracking-tight text-ink mb-8">Company Settings</h1>
      <form onSubmit={save} className="max-w-2xl space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`set-${f.key}`}>{f.label}</Label>
              <Input
                id={`set-${f.key}`}
                data-testid={`settings-field-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="rounded-none"
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label htmlFor="set-naics">NAICS Codes (comma separated)</Label>
            <Input
              id="set-naics"
              data-testid="settings-field-naics"
              value={Array.isArray(form.naics) ? form.naics.join(", ") : form.naics || ""}
              onChange={(e) => setForm({ ...form, naics: e.target.value })}
              className="rounded-none"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="set-certs">Certifications (comma separated)</Label>
            <Input
              id="set-certs"
              data-testid="settings-field-certifications"
              value={Array.isArray(form.certifications) ? form.certifications.join(", ") : form.certifications || ""}
              onChange={(e) => setForm({ ...form, certifications: e.target.value })}
              className="rounded-none"
            />
          </div>
        </div>
        <Button type="submit" data-testid="settings-save-btn" disabled={saving} className="rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </form>
    </div>
  );
};
